/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import "chart.js/auto";
// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React examples
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 PRO React routes
import routes from "routes";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import brandSNT from "assets/images/SNT/snt-logo.png"
import PrivateRoute from "ProtectedRoute";
import { useTeamValue } from "providers/TeamContext";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        if (route.anonymous) {
          return <Route exact path={route.route} element={route.component} key={route.key} />;
        }
        return (
          <Route
            exact
            path={route.route}
            element={<PrivateRoute>{route.component}</PrivateRoute>}
            key={route.key}
          />
        );
      }

      return null;
    });

  const { permissions, allowAll } = useTeamValue();

  const visibleRoute = routes.map(x => {
    if (x.type === "collapse" && (x.key === "inbound" || x.key === "outbound" || x.key === "setup" || x.key === "inventory" || x.key === "billing" || x.key === "return-order")) {
      const tempCollapse = x.collapse.map((y) => {
        const access = allowAll ||
          (Boolean(permissions[y.name]) && permissions[y.name].some(a => a === "view")) ||
          (y.name === "Adjustment" && Boolean(permissions["Stock Adjustment"]) && permissions["Stock Adjustment"].some(a => a === "view")) ||
          (y.name === "ASN" && Boolean(permissions["Advance Shipment Notice"]) && permissions["Advance Shipment Notice"].some(a => a === "view")) ||
          (y.name === "Inventory list" && (
            (Boolean(permissions["Inventory SKU List"]) && permissions["Inventory SKU List"].some(a => a === "view")) ||
            (Boolean(permissions["Inventory Location List"]) && permissions["Inventory Location List"].some(a => a === "view")) ||
            (Boolean(permissions["Division SKU"]) && permissions["Division SKU List"].some(a => a === "view"))
          )) ||
          (y.name === "Vendor SKU" && Boolean(permissions["Retail Barcode"]) && permissions["Retail Barcode"].some(a => a === "view")) ||
          (y.name === "Manifest to Etrax" && Boolean(permissions["Manifest to eTrax"]) && permissions["Manifest To eTrax"].some(a => a === "view")) ||
          (y.name === "Shipped API" && Boolean(permissions["Shipped Items"]) && permissions["Shipped Items"].some(a => a === "view")) ||
          (y.name === "Received API" && Boolean(permissions["Received Items"]) && permissions["Received Items"].some(a => a === "view")) ||
          (y.name === "Return Api" && Boolean(permissions["Return API"]) && permissions["Return API"].some(a => a === "view")) ||
          (y.name === "Putaway" && Boolean(permissions["Cancel Order"]) && permissions["Cancel Order"].some(a => a === "view")) ||
          (y.name === "Task Assignment" && Boolean(permissions["Task Assignment"]) && permissions["Task Assignment"] !== null && permissions["Task Assignment"].findIndex(b => b === "new") !== -1);
          if (!access) {
          return {
            ...y, collapse: y.collapse.map(z => {
              return { ...z, visible: true };
            })
          };
        } else {
          return {
            ...y, collapse: y.collapse.map(z => {
              const visible = allowAll ||
                (
                  (Boolean(permissions[y.name]) && permissions[y.name].some(a => z.key.includes(a))) ||
                  (Boolean(permissions[y.name]) && permissions[y.name].some(a => a === "new") && z.key.includes("add")) ||
                  (z.key.includes("management") && z.key !== "task-assignment-management" && z.key !== "inventory-location-management" && z.key !== "inventory-sku-management") ||
                  (z.key.includes("mgmt")) ||
                  (z.key.includes("upload") && Boolean(permissions[y.name]) && permissions[y.name] !== null && permissions[y.name].findIndex(b => b === "new") !== -1) ||
                  (z.key === "generate-physical-count" || z.key === "update-physical-count")
                ) ||
                (
                  (y.name === "Adjustment" && Boolean(permissions["Stock Adjustment"]) && permissions["Stock Adjustment"].some(a => z.key.includes(a))) ||
                  (y.name === "Adjustment" && Boolean(permissions["Stock Adjustment"]) && permissions["Stock Adjustment"].some(a => a === "new") && z.key.includes("add")) ||
                  (z.key.includes("management") && z.key !== "task-assignment-management" && z.key !== "inventory-location-management" && z.key !== "inventory-sku-management") ||
                  (z.key.includes("mgmt"))
                ) ||
                (
                  (y.name === "ASN" && Boolean(permissions["Advance Shipment Notice"]) && permissions["Advance Shipment Notice"].some(a => z.key.includes(a))) ||
                  (y.name === "ASN" && Boolean(permissions["Advance Shipment Notice"]) && permissions["Advance Shipment Notice"].some(a => a === "new") && z.key.includes("add")) ||
                  (z.key.includes("management") && z.key !== "task-assignment-management" && z.key !== "inventory-location-management" && z.key !== "inventory-sku-management") ||
                  (z.key.includes("mgmt"))
                ) ||
                (y.name === "Vendor SKU" && (z.key === "vendor-sku-mgmt" || z.key === "add-sku-retail-barcode") && Boolean(permissions["Retail Barcode"]) && permissions["Retail Barcode"].findIndex(b => b === "view") !== -1) ||
                (y.name === "Putaway" && z.key === "cancel-order" && Boolean(permissions["Cancel Order"]) && permissions["Cancel Order"].findIndex(b => b === "view") !== -1) ||
                (y.name === "Replenishment" && z.key === "update-replenishment" && Boolean(permissions["Replenishment"] && permissions["Replenishment"].findIndex(b => b === "edit") !== -1)) ||
                (y.name === "Shipped API" && Boolean(permissions["Shipped Items"]) && permissions["Shipped Items"].some(a => a === "view")) ||
                (y.name === "Received API" && Boolean(permissions["Received Items"]) && permissions["Received Items"].some(a => a === "view")) ||
                (y.name === "Return Api" && Boolean(permissions["Return API"]) && permissions["Return API"].some(a => a === "view")) ||
                (y.name === "Inventory list" && z.key === "inventory-sku-management" && (Boolean(permissions["Inventory SKU List"]) && permissions["Inventory SKU List"].findIndex(b => b === "view") !== -1)) ||
                (y.name === "Inventory list" && z.key === "inventory-location-management" && (Boolean(permissions["Inventory Location List"]) && permissions["Inventory Location List"].findIndex(b => b === "view") !== -1)) ||
                (y.name === "Inventory list" && z.key === "division-inventory-sku-management" && (Boolean(permissions["Division SKU List"]) && permissions["Division SKU List"].findIndex(b => b === "view") !== -1)) ||
                (z.key === "task-assignment-management" && Boolean(permissions["Task Assignment"]) && permissions["Task Assignment"] !== null && permissions["Task Assignment"].findIndex(b => b === "view") !== -1);
              return { ...z, visible: z.visible || !visible };
            })
          };
        }
      });
      return { ...x, collapse: tempCollapse };
    }
    else if (x.type === "collapse" && x.key === "report") {
      const tempCollapse = x.collapse.map((y) => {
        return {
          ...y, collapse: y.collapse.map(z => {
            const visible =
              allowAll ||
              (Boolean(permissions[`${z.name}_Report`]) && permissions[`${z.name}_Report`].some(a => a === "view")) ||
              ((z.name === "Pickslip Pick" || z.name === "Pickslip Pack") && Boolean(permissions[`${z.name} Scan_Report`]) && permissions[`${z.name} Scan_Report`].some(a => a === "view")) ||
              ((z.name === "Recv Serial Number") && Boolean(permissions[`Serial Number Listing_Report`]) && permissions[`Serial Number Listing_Report`].some(a => a === "view")) ||
              ((z.name === "Division Balance Check") && Boolean(permissions[`Division Inventories_Report`]) && permissions[`Division Inventories_Report`].some(a => a === "view")) ||
              ((z.name === "SKU M. Summary") && Boolean(permissions[`SKU Movement_Report`]) && permissions[`SKU Movement_Report`].some(a => a === "view")) ||
              ((z.name === "H.I. Summary") && Boolean(permissions[`Historical Inventory_Report`]) && permissions[`Historical Inventory_Report`].some(a => a === "view"));
            return { ...z, visible: z.visible || !visible };
          })
        }
      });
      return { ...x, collapse: tempCollapse };
    }
    else if (x.type === "collapse" && x.key === "dashboards") {
      const tempCollapse = x.collapse.map((z) => {
        const visible =
              allowAll ||
              (Boolean(permissions[`${z.name}_Dashboard`]) && permissions[`${z.name}_Dashboard`].some(a => a === "view"));
            return { ...z, visible: z.visible || !visible };
      });
      return { ...x, collapse: tempCollapse };
    }
    else if (x.type === "collapse" && x.key === "user") {
      const tempCollapse = x.collapse.map((y) => {
        return {
          ...y, collapse: y.collapse.map(z => {
            const visible =
              (Boolean(permissions["Team Role"]) && permissions["Team Role"].some(a => a === "access"));
            return { ...z, visible: z.visible || !visible };
          })
        }
      });
      return { ...x, collapse: tempCollapse };
    }
    else if (x.type === "collapse" && x.key === "kpi") {
      const tempCollapse = x.collapse.map((y) => {
        const access = allowAll ||
          (Boolean(permissions[y.name]) && permissions[y.name].some(a => a === "view")) ||
          (y.name === "KPI DashBoard" && Boolean(permissions["KPI Dashboard_Report"]) && permissions["KPI Dashboard_Report"].findIndex(b => b === "view") !== -1)
        if (!access) {
          return {
            ...y, collapse: y.collapse.map(z => {
              return { ...z, visible: true };
            })
          };
        } else {
          return {
            ...y, collapse: y.collapse.map(z => {
              const visible = allowAll ||
                (
                  (Boolean(permissions[y.name]) && permissions[y.name].some(a => z.key.includes(a))) ||
                  (Boolean(permissions[y.name]) && permissions[y.name].some(a => a === "new") && z.key.includes("add"))
                ) ||
                (
                  (z.key.includes("management") && z.key !== "task-assignment-management" && z.key !== "inventory-location-management" && z.key !== "inventory-sku-management") ||
                  (z.key.includes("mgmt"))
                ) ||
                (y.name === "KPI DashBoard" && z.key === "kpi-dashboard" && Boolean(permissions["KPI Dashboard_Report"]) && permissions["KPI Dashboard_Report"].findIndex(b => b === "view") !== -1);
              return { ...z, visible: z.visible || !visible };
            })
          };
        }
      });
      return { ...x, collapse: tempCollapse };
    }
    else return x;
  });

  function filterCollapseItems(items) {
    return items.reduce((result, item) => {
      if (item.type === 'collapse' && item.collapse) {
        const filteredCollapse = filterCollapseItems(item.collapse);

        // Check if all children in the collapse have visible: true
        const allVisible = item.collapse.every(child =>
          child.visible === true ||
          (child.collapse && child.collapse.every(smallerChild => smallerChild.visible === true))
        );

        if ((item.name === "Vendor SKU" || item.name === "SKU Pack Type") || filteredCollapse.length > 0 && !allVisible) {
          result.push({ ...item, collapse: filteredCollapse, 
            visible: (item.name === "Vendor SKU" || item.name === "SKU Pack Type")
            });
        }
      } else {
        result.push(item);
      }

      return result;
    }, []);
  }

  const tempRoute = filterCollapseItems(visibleRoute)

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const firstModuleGroup = tempRoute.find(
    x =>
      x.key !== "full-Name" &&
      Boolean(x.collapse) &&
      x.collapse.some(
        y =>
          (!y.visible && !y.collapse) ||
          (Boolean(y.collapse) && y.collapse.some(z => !z.visible))
      )
  );

  let firstModule = null;

  if (firstModuleGroup) {
    // find first failing child at level 1
    firstModule = firstModuleGroup.collapse.find(
      x =>
        (!x.visible && !x.collapse) ||
        (Boolean(x.collapse) && x.collapse.some(z => !z.visible))
    );

    // if that child has collapse, drill into it
    if (firstModule?.collapse) {
      const nested = firstModule.collapse.find(z => !z.visible);
      if (nested) {
        firstModule = nested;
      }
    }
  }

  const firstPageUrl = Boolean(firstModule) ? firstModule.route :
    direction === "rtl" ? "/dashboards/po-dashboard" : "/dashboards/sales-overall";

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              //brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brand={brandSNT}
              brandName="eHub"
              routes={tempRoute}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(tempRoute)}
          {/* <Route path="*" element={<Navigate to="/dashboards/po-dashboard" />} /> */}
          <Route path="*" element={<Navigate to={firstPageUrl} />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            //brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brand={brandSNT}
            brandName="eHub"
            routes={tempRoute}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {/* {configsButton} */}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(tempRoute)}
        {/* <Route path="*" element={<Navigate to="/dashboards/sales-overall" />} /> */}
        <Route path="*" element={<Navigate to={firstPageUrl} />} />
      </Routes>
    </ThemeProvider>
  );
}
